import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ExternalLink, Search } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageHeader } from "@/components/shared/page-header";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useBatchGoodsReceipts,
  useInventoryBatch,
  useInventoryBatchSearch,
  useInventoryMovements,
} from "@/features/inventory/api";
import {
  BATCH_STATUSES,
  MOVEMENT_KINDS,
  QUALITY_STATUSES,
  batchStatusTone,
  fmtQty,
  labelFor,
  qualityTone,
} from "@/features/inventory/constants";
import { isSupabaseConfigured } from "@/lib/supabase";
import { cn } from "@/lib/utils";

export function InventoryLookupPage() {
  return (
    <ErrorBoundary title="Lookup crashed">
      <LookupInner />
    </ErrorBoundary>
  );
}

function LookupInner() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { batch?: string; q?: string };
  const [q, setQ] = useState(search.q ?? "");
  const [selectedId, setSelectedId] = useState(search.batch ?? "");

  useEffect(() => {
    if (search.batch) setSelectedId(search.batch);
  }, [search.batch]);

  const { data: hits = [], isFetching: searching } = useInventoryBatchSearch(q);
  const { data: batch, isLoading: batchLoading } = useInventoryBatch(
    selectedId || undefined,
  );
  const { data: movements = [], isLoading: movesLoading } = useInventoryMovements(
    selectedId || undefined,
  );
  const { data: receipts = [] } = useBatchGoodsReceipts(selectedId || undefined);

  function selectBatch(id: string) {
    setSelectedId(id);
    void navigate({
      to: "/lookup",
      search: { batch: id, q: q.trim() || undefined },
      replace: true,
    });
  }

  if (!isSupabaseConfigured) {
    return (
      <EmptyState title="Supabase not configured" description="Configure Supabase for lookup." />
    );
  }

  return (
    <div className="space-y-3">
      <PageHeader
        title="Lookup"
        description="Trace a batch — stock, movements, PO and GRN."
      />

      <div className="rounded-lg border border-border bg-card p-3 space-y-2">
        <label className="text-2xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
          Search batch
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Batch number or material…"
            className="h-9 pl-8"
            autoFocus
          />
        </div>
        {q.trim() ? (
          <div className="max-h-56 overflow-auto rounded-md border border-border">
            {searching ? (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">Searching…</p>
            ) : hits.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">No matches</p>
            ) : (
              <ul className="divide-y divide-border">
                {hits.map((h) => (
                  <li key={h.id}>
                    <button
                      type="button"
                      className={cn(
                        "flex w-full items-start justify-between gap-2 px-3 py-2 text-left hover:bg-muted/50",
                        selectedId === h.id && "bg-muted/60",
                      )}
                      onClick={() => selectBatch(h.id)}
                    >
                      <div className="min-w-0">
                        <div className="font-mono text-xs font-medium">{h.batch_no}</div>
                        <div className="truncate text-2xs text-muted-foreground">
                          {[h.material_name, h.grade_name, h.location_code]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                      <div className="shrink-0 text-right tabular-nums text-2xs text-muted-foreground">
                        {fmtQty(h.weight)} T
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="text-2xs text-muted-foreground">Type at least one character to search.</p>
        )}
      </div>

      {!selectedId ? (
        <EmptyState
          title="Select a batch"
          description="Search above, or open a batch from Inventory."
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/inventory">Inventory list</Link>
            </Button>
          }
        />
      ) : batchLoading ? (
        <p className="py-10 text-center text-xs text-muted-foreground">Loading batch…</p>
      ) : !batch ? (
        <EmptyState title="Batch not found" description="Try another search." />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-mono text-base font-semibold">{batch.batch_no}</h2>
              <p className="text-xs text-muted-foreground">
                {[batch.material_name, batch.grade_name, batch.shape_label]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/inventory/$batchId" params={{ batchId: batch.id }}>
                <ExternalLink className="size-3.5" />
                Full detail
              </Link>
            </Button>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <InfoCard title="Stock">
              <Row label="Weight" value={`${fmtQty(batch.weight)} ${batch.uom_code ?? ""}`.trim()} />
              <Row label="Qty" value={fmtQty(batch.quantity)} />
              <Row
                label="Location"
                value={
                  batch.location_code
                    ? `${batch.location_code}${batch.location_name ? ` · ${batch.location_name}` : ""}`
                    : "—"
                }
              />
              <Row
                label="Status"
                value={
                  <StatusDot tone={batchStatusTone(batch.batch_status)}>
                    {labelFor(BATCH_STATUSES, batch.batch_status)}
                  </StatusDot>
                }
              />
              <Row
                label="QC"
                value={
                  <StatusDot tone={qualityTone(batch.quality_status)}>
                    {labelFor(QUALITY_STATUSES, batch.quality_status)}
                  </StatusDot>
                }
              />
            </InfoCard>

            <InfoCard title="Trail">
              <Row
                label="PO"
                value={
                  batch.purchase_order_id && batch.po_number ? (
                    <Link
                      to="/purchase/orders/$poId"
                      params={{ poId: batch.purchase_order_id }}
                      className="text-primary hover:underline"
                    >
                      {batch.po_number}
                    </Link>
                  ) : (
                    "—"
                  )
                }
              />
              <Row
                label="Vendor"
                value={batch.vendor_name ?? "—"}
              />
              {receipts.length === 0 ? (
                <Row label="GRN" value="—" />
              ) : (
                receipts.map((r) => (
                  <Row
                    key={r.receipt_id}
                    label="GRN"
                    value={
                      <span className="font-mono">
                        {r.receipt_no}
                        <span className="text-muted-foreground">
                          {" "}
                          · {fmtQty(r.weight)} T
                        </span>
                      </span>
                    }
                  />
                ))
              )}
            </InfoCard>

            <InfoCard title="Received">
              <Row
                label="At"
                value={format(parseISO(batch.received_at), "dd MMM yyyy HH:mm")}
              />
              <Row
                label="Dims"
                value={
                  [
                    batch.thickness != null ? `Thk ${fmtQty(batch.thickness)}` : null,
                    batch.width != null ? `W ${fmtQty(batch.width)}` : null,
                    batch.length != null ? `L ${fmtQty(batch.length)}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"
                }
              />
            </InfoCard>
          </div>

          <section className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="border-b border-border px-3 py-2">
              <h3 className="text-sm font-semibold">Movements</h3>
            </div>
            {movesLoading ? (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</p>
            ) : movements.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">No movements</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>When</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead className="text-right">Δ Wt</TableHead>
                    <TableHead className="text-right">After</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="tabular-nums text-xs text-muted-foreground">
                        {format(parseISO(m.movement_at), "dd MMM HH:mm")}
                      </TableCell>
                      <TableCell className="text-xs">{labelFor(MOVEMENT_KINDS, m.kind)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">
                        {fmtSigned(m.weight_delta)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-xs font-medium">
                        {fmtQty(m.weight_after)}
                      </TableCell>
                      <TableCell className="max-w-[12rem] truncate text-xs text-muted-foreground">
                        {m.notes ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card p-3 space-y-2">
      <h3 className="text-2xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
        {title}
      </h3>
      <dl className="space-y-1.5 text-xs">{children}</dl>
    </section>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium">{value}</dd>
    </div>
  );
}

function fmtSigned(n: number) {
  if (!n) return "0";
  const s = fmtQty(Math.abs(n));
  return n > 0 ? `+${s}` : `−${s}`;
}
